import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import Logo from '../components/Logo';
import Logger from '../utils/logger';
import apiService from '../services/api';
import { SuccessMessages, ErrorMessages, getApiErrorMessage } from '../constants/messages';
import { Button, Input } from '../components/ui';

interface LoginForm {
  email: string;
  password: string;
}

const LoginPage: React.FC = () => {
  // @ts-ignore -- TS2589: useTranslation type instantiation too deep (react-i18next + augmented resource types)
  const { t } = (useTranslation as any)('auth');
  const { login, loginWithGoogle, loginWithEntra } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [ssoOpen, setSsoOpen] = useState(false);
  const [ssoEmail, setSsoEmail] = useState('');
  const [ssoLoading, setSsoLoading] = useState(false);
  const googleLoginEnabled =
    (window as { _env_?: { REACT_APP_GOOGLE_LOGIN_ENABLED?: string } })._env_?.REACT_APP_GOOGLE_LOGIN_ENABLED === 'true';

  // Retour de Google ou d'Entra en erreur : OidcReturnPage renvoie ici avec le code.
  useEffect(() => {
    const st = location.state as { googleError?: string; oidcError?: string } | null;
    const code = st?.oidcError || st?.googleError;
    if (code) {
      const ns = code.startsWith('entra_') ? 'login.entraErrors' : 'login.googleErrors';
      toast.error(t(`${ns}.${code}`, { defaultValue: t(`${ns}.generic`) }));
      window.history.replaceState({}, '');
    }
  }, [location.state, t]);

  // Connexion SSO entreprise : l'adresse dit quelle organisation impose quoi.
  const onSso = async (e: React.FormEvent) => {
    e.preventDefault();
    const email = ssoEmail.trim().toLowerCase();
    if (!email) return;
    setSsoLoading(true);
    try {
      const disc = await apiService.ssoDiscover(email);
      if (!disc.sso_available || !disc.organisation_id) {
        toast.error(t('login.sso.notAvailable'));
        setSsoLoading(false);
        return;
      }
      await loginWithEntra(disc.organisation_id, undefined, email);
    } catch (error: unknown) {
      Logger.error('SSO discover failed', error);
      toast.error(t('login.entraErrors.entra_unavailable'));
      setSsoLoading(false);
    }
  };

  const onGoogle = async () => {
    setGoogleLoading(true);
    try {
      await loginWithGoogle();
    } catch (error: unknown) {
      Logger.error('Google authorize failed', error);
      toast.error(t('login.googleErrors.google_unavailable'));
      setGoogleLoading(false);
    }
  };
  const [loginError, setLoginError] = useState<string>(() => {
    const savedError = sessionStorage.getItem('loginError');
    if (savedError) {
      sessionStorage.removeItem('loginError');
      return savedError;
    }
    return '';
  });
  const { register, handleSubmit, formState: { errors } } = useForm<LoginForm>();

  const onSubmit = async (data: LoginForm, e?: React.BaseSyntheticEvent) => {
    e?.preventDefault();
    Logger.log('Form submit - preventing default');

    setLoading(true);
    setLoginError('');
    sessionStorage.removeItem('loginError');
    try {
      const result = await login(data.email, data.password);
      if (result.needsMfa) {
        // Skip the success toast — auth isn't complete until TOTP verifies.
        navigate('/login/mfa');
        return;
      }
      if (result.needsDeviceVerification) {
        // Skip the success toast — auth isn't complete until the email code verifies.
        navigate('/login/verify-device');
        return;
      }
      toast.success(SuccessMessages.LOGIN_SUCCESS);
    } catch (error: unknown) {
      const apiError = error as { response?: { status?: number; data?: { detail?: string } } };

      Logger.error('Login failed', error);

      // Use centralized error message helper
      const errorMessage = apiError.response?.status === 401
        ? ErrorMessages.AUTH_INVALID_CREDENTIALS
        : getApiErrorMessage(error);

      Logger.log('Setting error message:', errorMessage);

      sessionStorage.setItem('loginError', errorMessage);
      setLoginError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface-0 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        {/* Logo */}
        <div className="flex justify-center">
          <Logo size="large" />
        </div>
        <h2 className="mt-6 text-center text-3xl font-bold text-text-primary">
          {t('login.title')}
        </h2>
        <p className="mt-2 text-center text-sm text-text-secondary">
          {t('or', { ns: 'common' })}{' '}
          <Link
            to="/register"
            className="font-medium text-accent-400 hover:text-accent-300"
          >
            {t('login.newAccount')}
          </Link>
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-surface-2 py-8 px-4 shadow-card sm:rounded-card sm:px-10">
          {/* Affichage de l'erreur de connexion */}
          {loginError && (
            <div className="mb-4 bg-red-500/12 border border-red-500/30 text-red-400 px-4 py-3 rounded relative" role="alert">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <span className="font-medium">{loginError}</span>
                </div>
                <button
                  type="button"
                  onClick={() => setLoginError('')}
                  aria-label={t('login.closeError') as string}
                  className="ml-4 text-red-400 hover:text-red-300 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 rounded"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          )}

          <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
            {/* Email */}
            <Input
              {...register('email', {
                required: t('email.required', { ns: 'validation' }) as string,
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: t('email.invalid', { ns: 'validation' }) as string
                }
              })}
              id="email"
              type="email"
              label={t('login.email') as string}
              autoComplete="email"
              aria-required="true"
              placeholder={t('login.emailPlaceholder') as string}
              error={errors.email?.message}
            />

            {/* Password */}
            <Input
              {...register('password', {
                required: t('password.required', { ns: 'validation' }) as string,
                minLength: {
                  value: 8,
                  message: t('password.minLength', { ns: 'validation', count: 8 }) as string
                }
              })}
              id="password"
              type="password"
              label={t('login.password') as string}
              autoComplete="current-password"
              aria-required="true"
              placeholder={t('login.passwordPlaceholder') as string}
              error={errors.password?.message}
            />

            {/* Remember me & Forgot password */}
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  className="h-4 w-4 text-accent-600 focus:ring-accent-500 border-border-1 rounded bg-surface-2"
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-text-secondary">
                  {t('login.rememberMe')}
                </label>
              </div>

              <div className="text-sm">
                <Link to="/forgot-password" className="font-medium text-accent-400 hover:text-accent-300">
                  {t('login.forgotPassword')}
                </Link>
              </div>
            </div>

            {/* Submit Button */}
            <div>
              <Button
                type="submit"
                variant="primary"
                size="md"
                loading={loading}
                disabled={loading}
                className="w-full"
              >
                {loading ? t('login.submitting') : t('login.submit')}
              </Button>
            </div>
          </form>

          {/* Divider */}
          {googleLoginEnabled && (
          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border-1" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-surface-2 text-text-tertiary">{t('login.continueWith')}</span>
              </div>
            </div>
          </div>
          )}

          {/* Connexion SSO entreprise (Entra ID) */}
          <div className="mt-6">
            {!ssoOpen ? (
              <button
                type="button"
                onClick={() => setSsoOpen(true)}
                className="w-full text-sm font-medium text-accent-400 hover:text-accent-300"
              >
                {t('login.sso.button')}
              </button>
            ) : (
              <form onSubmit={onSso} className="space-y-3 p-4 rounded-card border border-border-1 bg-surface-3">
                <p className="text-sm text-text-secondary">{t('login.sso.hint')}</p>
                <input
                  type="email"
                  value={ssoEmail}
                  onChange={(e) => setSsoEmail(e.target.value)}
                  placeholder={t('login.sso.emailPlaceholder') as string}
                  className="block w-full border border-border-1 rounded-md py-2 px-3 bg-surface-2 text-text-primary text-sm focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                  autoFocus
                  required
                />
                <div className="flex space-x-2">
                  <Button type="submit" variant="secondary" size="md" className="flex-1" loading={ssoLoading} disabled={ssoLoading}>
                    {t('login.sso.submit')}
                  </Button>
                  <button type="button" onClick={() => setSsoOpen(false)} className="text-sm text-text-tertiary hover:text-text-secondary px-3">
                    {t('login.sso.cancel')}
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* Social Login */}
          {googleLoginEnabled && (
          <div className="mt-6">
            <Button
              type="button"
              variant="secondary"
              size="md"
              className="w-full"
              onClick={onGoogle}
              loading={googleLoading}
              disabled={loading || googleLoading}
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="currentColor"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="currentColor"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="currentColor"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              <span className="ml-2">{t('login.google')}</span>
            </Button>
          </div>
          )}

        </div>
      </div>

      {/* Back to home */}
      <div className="mt-8 text-center">
        <Link
          to="/"
          className="font-medium text-accent-400 hover:text-accent-300"
        >
          {t('login.backToHome')}
        </Link>
      </div>
    </div>
  );
};

export default LoginPage;
